import React, { useEffect, useId, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'

import PreJoinBackgroundPicker from './PreJoinBackgroundPicker'
import BackgroundProblemNotice from './BackgroundProblemNotice'
import { usePreviewHandoverUnderlay } from '../hooks/usePreviewHandoverUnderlay'
import { SELF_VIEW_MIRROR_STYLE } from '../utils/selfViewMirror'
import {
  previewPreparingStyle,
  previewSettledStyle,
  previewCoverSx,
  previewProgressSx,
  previewUnderlayStyle,
  PREVIEW_EFFECT_TRANSITION,
} from './styles/PermissionsDialog.styles'
import {
  backgroundProblemHoldsCamera,
  preloadBackgroundPresetImages,
  type BackgroundEffect,
  type BackgroundProblem,
} from '../services/liveKitVideoBackground'
import {
  backgroundProblemTooltip,
  type BackgroundProblemAction,
} from '../services/backgroundProblemCopy'
import { PREVIEW_PAINT_TIMEOUT_MS, whenVideoPainted } from '../utils/whenVideoPainted'

export interface BackgroundEffectsDialogProps {
  open: boolean
  onClose: () => void
  /** Whatever is currently on screen — processed when an effect is active. */
  previewStream: MediaStream | null
  /**
   * The raw view a pick was made over, which may stay beneath the preview
   * while the processed stream takes it over (see useMediaDevices).
   */
  handoverFrom?: MediaStream | null
  cameraEnabled: boolean
  /** True while the chosen effect is not on screen: the preview is covered. */
  effectPending?: boolean
  /** True while there is effect work under way, for the progress bar. */
  effectWorking?: boolean
  /** Why the chosen effect is not showing, if anything. */
  problem?: BackgroundProblem | null
  onProblemAction?: (action: BackgroundProblemAction) => void
  value: BackgroundEffect
  onChange: (effect: BackgroundEffect) => void
  /** What the preview is showing; see {@link PreJoinBackgroundPicker}. */
  showing?: BackgroundEffect
  disabled?: boolean
}

/**
 * Backgrounds and effects, as a focused dialog: preview on the left, choices on
 * the right (SC-158).
 *
 * Previously this lived inline in the device-selection column, which stretched
 * that column far past the video and pushed the camera and microphone controls
 * out of view. Giving it its own surface keeps the settings dialog compact and
 * gives the choices enough room to be judged, which matters when most of them
 * are images.
 *
 * The preview binds the same stream object the settings dialog shows, so no
 * second processor — and no second WebGL context — is created for it.
 */
export const BackgroundEffectsDialog: React.FC<BackgroundEffectsDialogProps> = ({
  open,
  onClose,
  previewStream,
  handoverFrom = null,
  cameraEnabled,
  effectPending = false,
  effectWorking = false,
  problem = null,
  onProblemAction,
  value,
  onChange,
  showing,
  disabled = false,
}) => {
  /**
   * Held as state, not a ref, because MUI's Dialog portals its content in a
   * later commit than the one that runs this effect — an object ref is still
   * null at that point, and with the stream already present the deps would
   * never change again, so the preview would simply stay blank.
   */
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null)

  // Warm the plates as soon as the picker is opened, so switching to one does
  // not wait on a download with no background showing.
  useEffect(() => {
    if (open) preloadBackgroundPresetImages()
  }, [open])

  /**
   * The stream the element has painted a frame of — the identity, not a boolean.
   *
   * A boolean lags one render: the swap to the processed stream re-renders with
   * `effectPending` already false while the effect that would reset the boolean
   * has not run, so the cover lifts for one commit over a blank element. That is
   * the black frame. Comparing identities makes the answer correct in the very
   * render the stream changes.
   *
   * Kept with the element it was painted on. This component stays mounted with
   * the Join screen while the Dialog unmounts its content on close, so a reopen
   * brings a new, blank element; without the element, the old one's raw frame
   * would let the new one take the raw camera as its underlay (R13-B1).
   */
  const [painted, setPainted] = useState<{
    element: HTMLVideoElement
    stream: MediaStream
  } | null>(null)
  const paintedStream = painted !== null && painted.element === videoElement ? painted.stream : null

  useEffect(() => {
    if (!videoElement) return
    // No reset: `previewPainted` compares identities, so it is already false for this
    // stream until markPainted records it.
    videoElement.srcObject = previewStream
    if (!previewStream) return

    videoElement.play().catch(() => {
      /* autoplay refusal leaves a still frame; not worth surfacing here */
    })

    let settled = false
    const markPainted = () => {
      if (settled) return
      settled = true
      setPainted({ element: videoElement, stream: previewStream })
    }

    // The first frame requestVideoFrameCallback reports as composited, the only
    // signal that means "not black". `loadeddata` is the fallback where it is
    // unavailable.
    const cancelPaintWait = whenVideoPainted(
      videoElement,
      { frames: 1, fallbackEvents: ['loadeddata'] },
      markPainted
    )
    // Bounded: a camera that never paints must not hide the preview forever.
    const timeout = setTimeout(markPainted, PREVIEW_PAINT_TIMEOUT_MS)

    return () => {
      settled = true
      clearTimeout(timeout)
      cancelPaintWait()
    }
  }, [videoElement, previewStream])

  const previewPainted = previewStream !== null && paintedStream === previewStream

  /**
   * A handover this element was part of: it had painted `from` when the
   * processed stream `to` replaced it. The hook ends its handover as soon as
   * the Join preview has painted `to`, which may be a frame before this
   * element has; this lets the element finish its own, with the raw view it
   * was showing beneath, rather than dropping to the cover for that frame.
   */
  const [ownHandover, setOwnHandover] = useState<{
    element: HTMLVideoElement
    from: MediaStream
    to: MediaStream
  } | null>(null)
  useEffect(() => {
    if (!videoElement || handoverFrom === null || previewStream === null) return
    if (previewStream === handoverFrom || paintedStream !== handoverFrom) return
    setOwnHandover((current) =>
      current?.element === videoElement &&
      current.from === handoverFrom &&
      current.to === previewStream
        ? current
        : { element: videoElement, from: handoverFrom, to: previewStream }
    )
  }, [videoElement, handoverFrom, previewStream, paintedStream])

  /**
   * The raw view a pick was made over, beneath the preview while the processed
   * stream replaces it: what the hook offers, or the handover this element is
   * still finishing. Either way only while this element last painted that
   * very view, so a dialog opened later (a new element) never gets one, and
   * never once a problem covers the preview.
   */
  const ownHandoverFrom =
    !effectPending &&
    ownHandover !== null &&
    ownHandover.element === videoElement &&
    ownHandover.to === previewStream
      ? ownHandover.from
      : null
  const underlayFrom = handoverFrom ?? ownHandoverFrom
  const [underlayElement, setUnderlayElement] = useState<HTMLVideoElement | null>(null)
  const underlayStream =
    underlayFrom !== null && paintedStream === underlayFrom ? underlayFrom : null
  const underlayShowing = usePreviewHandoverUnderlay(underlayElement, underlayStream, previewStream)

  // With an effect selected and nothing processed yet there is no stream to
  // bind, but there is a camera: covered, not "your camera is off".
  const showVideo = cameraEnabled && (Boolean(previewStream) || effectPending)
  const notReady = effectPending || !previewPainted
  const covered = showVideo && notReady && !underlayShowing
  /** The words on the cover, which the notice beside it then leaves out. */
  const problemMessageId = useId()
  const problemOnPreview = covered && problem !== null && backgroundProblemHoldsCamera(problem)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Backgrounds and effects
        <IconButton onClick={onClose} aria-label="Close backgrounds and effects" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.25fr) minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              // Keeps the underlay's negative z-index inside this box.
              isolation: 'isolate',
              aspectRatio: '16 / 9',
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="video"
              ref={setVideoElement}
              autoPlay
              playsInline
              muted
              data-testid="background-effects-preview"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              // Mirrored like the join screen and the in-game own tile, so the
              // self view looks the same everywhere. See utils/selfViewMirror.
              style={{
                ...SELF_VIEW_MIRROR_STYLE,
                display: showVideo ? 'block' : 'none',
                transition: PREVIEW_EFFECT_TRANSITION,
                ...(notReady ? previewPreparingStyle : previewSettledStyle),
              }}
            />
            {/* Beneath the preview: the raw view a pick was made over, while the
                processed stream takes the preview over. */}
            <video
              ref={setUnderlayElement}
              autoPlay
              playsInline
              muted
              aria-hidden
              data-testid="background-effects-handover-underlay"
              style={{
                ...previewUnderlayStyle,
                display: showVideo && underlayStream ? 'block' : 'none',
              }}
            />
            {covered && (
              // Flat and opaque: never a frozen frame, never the room.
              <Box data-testid="background-effects-cover" sx={previewCoverSx}>
                {problem && backgroundProblemHoldsCamera(problem) && (
                  <Typography
                    variant="body2"
                    id={problemMessageId}
                    role="alert"
                    data-testid="background-effects-cover-reason"
                  >
                    {backgroundProblemTooltip(problem, 'join')}
                  </Typography>
                )}
              </Box>
            )}
            {effectWorking && showVideo && (
              <LinearProgress
                data-testid="background-effects-progress"
                aria-label="Applying background"
                sx={previewProgressSx}
              />
            )}
            {!showVideo && (
              <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}>
                <VideocamOffIcon />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Your camera is off
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ maxHeight: { md: 420 }, overflowY: 'auto', pr: 0.5 }}>
            {problem && onProblemAction && (
              <Box sx={{ mb: 1.5 }}>
                <BackgroundProblemNotice
                  problem={problem}
                  onAction={onProblemAction}
                  // The choices are right below.
                  hiddenActions={['pick-another']}
                  // Said on the preview already; here, only what can be done.
                  messageShownAt={problemOnPreview ? problemMessageId : undefined}
                />
              </Box>
            )}
            <PreJoinBackgroundPicker
              value={value}
              onChange={onChange}
              showing={showing}
              cameraEnabled={cameraEnabled}
              disabled={disabled}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default BackgroundEffectsDialog
